<div class="sync-history-container view">
    @if (isLoading) {
        <div class="loading">
            <mat-spinner color="accent" diameter="40"></mat-spinner>
            <p>Loading sync history...</p>
        </div>
    }

    @if (error) {
        <div class="error-message">
            <mat-icon>error</mat-icon>
            <p>{{ error }}</p>
        </div>
    }

  <mat-card class="cardPlain">
    <mat-card-header>
      <div class="section-header d-flex flex-col gap-4">
        <app-breadcrumbs [items]="breadcrumbs"></app-breadcrumbs>
        <h1 class="m-0 f-w-500">Sync History</h1>
        <p class="m-0 help-text">View and inspect your HubSpot sync history.</p>
      </div>
      <span class="spacer"></span>
    </mat-card-header>

    <mat-card-content class="d-flex flex-col gap-24">
      <form [formGroup]="filterForm" (ngSubmit)="applyFilters()" class="filter-controls form-no-subscripts">
        <div class="search-field">
          <mat-form-field appearance="outline" class="w-100">
            <mat-icon matPrefix>search</mat-icon>
            <mat-label>Company</mat-label>
            <input matInput type="text" formControlName="companyName" placeholder="Search by company name" />
          </mat-form-field>
        </div>

        <div class="status-select">
          <mat-form-field appearance="outline" class="w-100">
            <mat-icon matPrefix>filter_list</mat-icon>
            <mat-label>Status</mat-label>
            <mat-select formControlName="status">
              <mat-option value="">All</mat-option>
              <mat-option value="success">Success</mat-option>
              <mat-option value="partial">Partial</mat-option>
              <mat-option value="failed">Failed</mat-option>
              <mat-option value="in_progress">In progress</mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <div class="status-select">
          <mat-form-field appearance="outline" class="w-100">
            <mat-icon matPrefix>tune</mat-icon>
            <mat-label>Sync type</mat-label>
            <mat-select formControlName="syncType">
              <mat-option value="">All</mat-option>
              <mat-option value="manual">Manual</mat-option>
              <mat-option value="scheduled">Scheduled</mat-option>
              <mat-option value="filter">Filter</mat-option>
              <mat-option value="individual">Individual</mat-option>
              <mat-option value="daily">Daily</mat-option>
              <mat-option value="import">Import</mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <div class="date-field">
          <mat-form-field appearance="outline" class="w-100">
            <mat-icon matPrefix>event</mat-icon>
            <mat-label>Start date</mat-label>
            <input matInput [matDatepicker]="startPicker" formControlName="startDate" (click)="startPicker.open()" />
            <mat-datepicker #startPicker></mat-datepicker>
          </mat-form-field>
        </div>

        <div class="date-field">
          <mat-form-field appearance="outline" class="w-100">
            <mat-icon matPrefix>event</mat-icon>
            <mat-label>End date</mat-label>
            <input matInput [matDatepicker]="endPicker" formControlName="endDate" (click)="endPicker.open()" />
            <mat-datepicker #endPicker></mat-datepicker>
          </mat-form-field>
        </div>

        <div class="filter-actions">
          <button
            mat-stroked-button
            color="primary"
            type="submit"
            [disabled]="isLoading"
            class="d-inline-flex align-items-center gap-6"
          >
            <mat-icon>refresh</mat-icon>
            Refresh
          </button>

          <button
            mat-stroked-button
            color="primary"
            type="button"
            (click)="resetFilters()"
            [disabled]="isLoading"
            class="d-inline-flex align-items-center gap-6"
          >
            <mat-icon>clear</mat-icon>
            Clear filters
          </button>
        </div>
      </form>

      <div class="table-responsive">
        <table [dataSource]="dataSource" class="webinars-table" [class.is-empty]="!dataSource.data.length" mat-table>
          <ng-container matColumnDef="companyName">
            <th mat-header-cell *matHeaderCellDef>Company</th>
            <td mat-cell *matCellDef="let sync">
              <button
                type="button"
                class="table-row-action"
                [attr.aria-label]="'Open sync for ' + (sync.companyName || 'All companies') + ', ' + statusLabel(sync.status) + ', started ' + (sync.startedAt | date: 'medium')"
                (click)="$event.stopPropagation(); openDetails(sync)"
              >
                <span class="company-name">{{ sync.companyName || 'All companies' }}</span>
              </button>
            </td>
          </ng-container>

          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef>Status</th>
            <td mat-cell *matCellDef="let sync">
              <span class="status-pill" [ngClass]="getStatusClass(sync.status)">
                <mat-icon>{{ statusIcon(sync.status) }}</mat-icon>
                {{ statusLabel(sync.status) }}
              </span>
            </td>
          </ng-container>

          <ng-container matColumnDef="syncType">
            <th mat-header-cell *matHeaderCellDef>Type</th>
            <td mat-cell *matCellDef="let sync">{{ formatSyncType(sync.syncType) }}</td>
          </ng-container>

          <ng-container matColumnDef="startedAt">
            <th mat-header-cell *matHeaderCellDef>Started</th>
            <td mat-cell *matCellDef="let sync">{{ sync.startedAt | date: 'medium' }}</td>
          </ng-container>

          <ng-container matColumnDef="completedAt">
            <th mat-header-cell *matHeaderCellDef>Completed</th>
            <td mat-cell *matCellDef="let sync">
              {{ sync.completedAt ? (sync.completedAt | date: 'medium') : 'N/A' }}
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns" (click)="openDetails(row)"></tr>

          <ng-container matColumnDef="footer">
            <td mat-footer-cell *matFooterCellDef class="table-footer-cell" [attr.colspan]="displayedColumns.length">
              <div class="table-footer-content">
                <div class="total-count">Total <span class="amount">{{ dataSource.data.length }}</span></div>
                <mat-paginator
                  [length]="dataSource.data.length"
                  [pageSize]="10"
                  [hidePageSize]="true"
                  [showFirstLastButtons]="true"
                ></mat-paginator>
              </div>
            </td>
          </ng-container>

          <tr mat-footer-row *matFooterRowDef="['footer']" [hidden]="!dataSource.data.length"></tr>
        </table>
      </div>

        @if (!isLoading && !dataSource.data.length) {
            <div aria-live="polite" class="empty-state" role="region">
                <h3 class="title">{{ hasActiveFilters ? 'No results' : 'No sync history yet' }}</h3>
                <p class="description">
                    {{
            hasActiveFilters
                ? 'Try adjusting or clearing your filters to see more sync events.'
                : 'Sync runs will appear here once your integration starts syncing.'
                    }}
                </p>
                <div class="actions">
                    @if (hasActiveFilters) {
                        <button
                                (click)="resetFilters()"
                                color="primary"
                                mat-stroked-button
                                type="button"
                        >
                            Clear filters
                        </button>
                    }
                </div>
            </div>
        }
    </mat-card-content>
  </mat-card>
</div>
